import React, { useState } from "react";
import { Card, Alert, Spinner, Button } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

const ResultDisplay = ({ 
  isLoading, 
  result, 
  error, 
  title = "Results",
  type = "markdown", // "text", "markdown", "list"
  showCopyButton = true,
  showInputParameters = true,
  inputParameters = null,
  inputParametersLabel = "Generated with",
  containerStyle = {},
  showAsCard = false,
  className = "",
  onCopy = null // Callback for copy notifications
}) => {

  if (!isLoading && !result && !error) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result);
      if (onCopy) {
        onCopy("Content copied!");
      }
    } catch (err) {
      console.error("Failed to copy content: ", err);
      if (onCopy) {
        onCopy("Failed to copy content", "error");
      }
    }
  };

  const renderInputParameters = () => {
    if (!showInputParameters || !inputParameters) return null;

    return (
      <div className="px-3 py-2 bg-light border rounded mb-2">
        <small className="text-muted fw-bold d-block mb-2">
          {inputParametersLabel}:
        </small>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: '4px' }}>
          {Object.entries(inputParameters).map(([key, value]) => {
            if (!value || key === "sourceType") return null;

            let displayKey = key;
            let displayValue = value;

            // Format key names
            switch (key) {
              case "keyword":
                displayKey = "Keyword";
                break;
              case "brand":
                displayKey = "Brand";
                break;
              case "title":
                displayKey = "Title";
                break;
              case "model":
                displayKey = "Model";
                break;
              case "wordCount":
                displayKey = "Words";
                break;
              case "seo_outline":
                displayKey = "Outline";
                break;
              case "primary_keyword":
                displayKey = "Keyword";
                break;
              default:
                displayKey = key.charAt(0).toUpperCase() + key.slice(1);
            }

            // Format values - truncate long values
            if (typeof displayValue === 'string' && displayValue.length > 30) {
              displayValue = displayValue.substring(0, 30) + "...";
            }

            return (
              <span 
                key={key} 
                className="badge bg-secondary text-white me-1 mb-1"
                style={{ 
                  fontSize: '11px',
                  maxWidth: '200px',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  display: 'inline-block',
                  lineHeight: '1.2'
                }}
                title={`${displayKey}: ${value}`} // Show full value on hover
              >
                <strong>{displayKey}:</strong> {displayValue}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <div className={className}>
      {/* Input Parameters */}
      {renderInputParameters()}

      {/* Header with Copy Button */}
      {result && !isLoading && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0 fw-bold">{title}</h6>
          {showCopyButton && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCopy}
            >
              Copy
            </Button>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">Generating content...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert variant="danger" className="mb-0">
          <small>{error}</small>
        </Alert>
      )}

      {/* Result Content */}
      {result && !isLoading && (
        <div 
          className="border rounded p-3 bg-light"
          style={{ 
            minHeight: "300px", 
            maxHeight: "500px", 
            overflowY: "auto",
            fontSize: "13px",
            lineHeight: "1.6",
            scrollbarWidth: "thin",
            scrollbarColor: "#6c757d #f8f9fa",
            ...containerStyle
          }}
        >
          {type === "markdown" && (
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h4 className="text-primary fw-bold mb-3 mt-2" style={{ fontSize: "18px" }}>
                    {children}
                  </h4>
                ),
                h2: ({ children }) => (
                  <h5 className="text-primary fw-bold mb-3 mt-4" style={{ fontSize: "16px" }}>
                    {children}
                  </h5>
                ),
                h3: ({ children }) => (
                  <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "15px" }}>
                    {children}
                  </h6>
                ),
                ul: ({ children }) => (
                  <ul className="mb-3" style={{ paddingLeft: "20px" }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-3" style={{ paddingLeft: "20px" }}>
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="mb-1" style={{ fontSize: "13px" }}>
                    {children}
                  </li>
                ),
                strong: ({ children }) => (
                  <strong className="text-dark fw-bold">{children}</strong>
                ),
                p: ({ children }) => (
                  <p className="mb-2" style={{ fontSize: "13px" }}>
                    {children}
                  </p>
                )
              }}
            >
              {result}
            </ReactMarkdown>
          )}
          
          {type === "list" && Array.isArray(result) && (
            <div>
              {result.map((item, index) => (
                <div key={index} className="mb-2 p-2 bg-light rounded">
                  <small className="text-muted">#{index + 1}</small>
                  <div>{item}</div>
                </div>
              ))}
            </div>
          )}
          
          {type === "text" && (
            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
              {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );

  // Return as card or plain content based on showAsCard prop
  if (showAsCard) {
    return (
      <Card className="mt-3 border-0 shadow-sm">
        <Card.Header className="bg-light border-0">
          <h6 className="mb-0 fw-bold text-primary">{title}</h6>
        </Card.Header>
        <Card.Body>
          {renderContent()}
        </Card.Body>
      </Card>
    );
  }

  return renderContent();
};

export default ResultDisplay;
